import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { anglesService } from '../services/angles.service';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type {
  DeleteConflictStrategy,
  ProjectAngle,
} from '@/types/project-angle';

// Query Keys
export const projectAngleKeys = {
  all: ['project-angles'] as const,
  lists: () => [...projectAngleKeys.all, 'list'] as const,
  list: (projectId: string) =>
    [...projectAngleKeys.lists(), projectId] as const,
  details: () => [...projectAngleKeys.all, 'detail'] as const,
  detail: (angleId: string) =>
    [...projectAngleKeys.details(), angleId] as const,
};

/**
 * Hook to fetch all project angles
 */
export function useProjectAngles(projectId: string) {
  return useQuery({
    queryKey: projectAngleKeys.list(projectId),
    queryFn: () => anglesService.listProjectAngles(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating a new project angle
 */
export function useCreateProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'create'],
    mutationFn: async ({
      fileName,
      md5,
      contentType,
      width,
      height,
      aspectRatio,
    }: {
      fileName: string;
      md5: string;
      contentType: string;
      width: number;
      height: number;
      aspectRatio: string;
    }) => {
      return anglesService.createProjectAngle(
        projectId,
        fileName,
        md5,
        contentType,
        width,
        height,
        aspectRatio
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for confirming a project angle upload
 */
export function useConfirmProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'confirm'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.confirmAngle(angleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for overriding a false "different building" rejection ("It's my
 * building"). The server unlocks the angle (validation flips back to Valid);
 * we invalidate the project-angles list AND every concept query (the concept
 * editor reads `angle.meta.validation` from the concept response) so the
 * rejection banner clears across all views.
 */
export function useConfirmBuildingAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'confirm-building'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.confirmBuilding(angleId);
    },
    onSuccess: () => {
      invalidateAfterPromotion(queryClient, projectId);
    },
  });
}

/**
 * Claim a routed intake as a view on the `/uploads` contract
 * (docs/upload/ADR-002). The same action as {@link useConfirmBuildingAngle},
 * one contract newer: nothing was soft-deleted there, so the server creates the
 * view rather than restoring one, and removes the reference it had made.
 */
export function usePromoteUpload(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['uploads', 'promote'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (uploadId: string) =>
      uploadsService.promote(projectId, uploadId),
    onSuccess: () => {
      invalidateAfterPromotion(queryClient, projectId);
    },
  });
}

/**
 * What a promotion changes, whichever contract ran it: a view appeared, the
 * reference it was filed as is gone, and the project's `view_promotion` rescue
 * flag (with its counters) has flipped — so the Brief banner and the
 * Set-up-design hint clear immediately.
 */
function invalidateAfterPromotion(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string
): void {
  queryClient.invalidateQueries({ queryKey: projectAngleKeys.list(projectId) });
  queryClient.invalidateQueries({ queryKey: ['concepts'] });
  queryClient.invalidateQueries({
    queryKey: cacheKeys.references.listsByProject(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: cacheKeys.projects.detail(projectId),
  });
}

/**
 * Hook for updating a project angle (name/description)
 */
export function useUpdateProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'update'],
    meta: {
      lockId: (vars: unknown) => (vars as { angleId: string }).angleId,
    },
    mutationFn: async ({
      angleId,
      updates,
    }: {
      angleId: string;
      updates: { name?: string | null; description?: string | null };
    }) => {
      return anglesService.updateAngle(angleId, updates);
    },
    onMutate: async ({ angleId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      // Snapshot the previous value
      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically update to the new value
      if (previousAngles) {
        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          previousAngles.map((angle) =>
            angle.id === angleId
              ? {
                  ...angle,
                  attributes: {
                    ...angle.attributes,
                    name:
                      updates.name !== undefined
                        ? updates.name
                        : angle.attributes.name,
                    description:
                      updates.description !== undefined
                        ? updates.description
                        : angle.attributes.description,
                  },
                }
              : angle
          )
        );
      }

      return { previousAngles };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for deleting a shared view from the library.
 *
 * A view is shared across all concepts, so deletion fans out project-wide (and
 * may drop the angle from the library) — there is intentionally NO optimistic
 * hide / undo for this cross-concept action (it is dialog-confirmed). The
 * mutation rejects with a 409 ApiClientError when the server needs a
 * `conflict_strategy`; the caller catches it, shows the conflict dialog, and
 * retries with the chosen strategy.
 */
export function useDeleteProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'delete'],
    meta: { lockId: (vars: unknown) => (vars as { angleId: string }).angleId },
    mutationFn: async ({
      angleId,
      strategy,
    }: {
      angleId: string;
      strategy?: DeleteConflictStrategy;
    }) => {
      return anglesService.deleteAngle(angleId, strategy);
    },
    onSuccess: () => {
      // Removal fans out to sibling concepts and may drop the angle from the
      // library. Invalidate the library list AND every concept query (concept
      // angle lists ride under the ['concepts'] root) so all views reconcile.
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
      queryClient.invalidateQueries({ queryKey: ['concepts'] });
    },
  });
}

/**
 * Hook for setting an angle as main
 */
export function useSetMainAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'set-main'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.setMainAngle(projectId, angleId);
    },
    onMutate: async (angleId) => {
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically update main status
      if (previousAngles) {
        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          previousAngles.map((angle) => ({
            ...angle,
            attributes: {
              ...angle.attributes,
              is_main: angle.id === angleId,
            },
          }))
        );
      }

      return { previousAngles };
    },
    onError: (_err, _angleId, context) => {
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for reordering project angles
 */
export function useReorderProjectAngles(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // Bulk reorder — no single id to lock against; lock factory skips when
    // `mutationKey[2]` is absent. The post-success invalidate refetches the
    // canonical order, so a racing WS event resolves there.
    mutationKey: ['angles', 'reorder'],
    mutationFn: async (angleIds: string[]) => {
      return anglesService.reorderProjectAngles(projectId, angleIds);
    },
    onMutate: async (angleIds) => {
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically reorder angles
      if (previousAngles) {
        const angleMap = new Map(previousAngles.map((a) => [a.id, a]));
        const reorderedAngles = angleIds
          .map((id, index) => {
            const angle = angleMap.get(id);
            if (!angle) return null;
            return {
              ...angle,
              attributes: {
                ...angle.attributes,
                order: index,
                is_main: index === 0, // First angle becomes main
              },
            };
          })
          .filter((a): a is ProjectAngle => a !== null);

        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          reorderedAngles
        );
      }

      return { previousAngles };
    },
    onError: (_err, _angleIds, context) => {
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}
